// import type { AxiosInstance } from 'axios';
import { detectDeviceType } from './device';

/* -----------------------------------------------------------
 *  TIPOS
 * --------------------------------------------------------- */

type DeviceType = ReturnType<typeof detectDeviceType>;

interface UAInfo {
  ua: string;
  browserName: string;
  browserVersion?: string;
  osName: string;
  osVersion?: string;
  deviceBrand?: string;
  deviceModel?: string;
  deviceType: DeviceType;
}

interface ScreenInfo {
  innerWidth?: string;
  innerHeight?: string;
  screenWidth?: string;
  screenHeight?: string;
  devicePixelRatio?: string;
}

interface SystemInfo {
  timezone: string;
  hardwareConcurrency: number | null;
  deviceMemory: number | null;
  touchPoints: number | null;
}

interface NetworkInfo {
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
}

interface AutomationSignals {
  webdriver: boolean;
  pluginCount: number;
  languagesCount: number;
  platform?: string;
  brands?: string[];
  webglVendor?: string;
  webglRenderer?: string;
}

interface AntiBotMeta {
  uaInfo: UAInfo;
  screen: ScreenInfo;
  system: SystemInfo;
  network: NetworkInfo;
  signals: AutomationSignals;
  keywords: string[];
  flags: {
    headless: boolean;
    automatedClient: boolean;
    inconsistentMobile: boolean;
    improbableResolution: boolean;
    suspiciousRenderer: boolean;
  };
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high';
  reasons: string[];
}

/* -----------------------------------------------------------
 *  HELPERS
 * --------------------------------------------------------- */

type ExtendedNavigator = Navigator & {
  webdriver?: boolean;
  plugins?: ArrayLike<unknown>;
  languages?: readonly string[];
  userAgentData?: { platform?: string; brands?: { brand: string }[] };
  deviceMemory?: number;
  hardwareConcurrency?: number;
  maxTouchPoints?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
};

/* -----------------------------------------------------------
 *  UA PARSER (Corregido)
 * --------------------------------------------------------- */

const parseBrowser = (
  ua: string,
): { browserName: string; browserVersion?: string } => {
  const edge = /Edg(e|A|IOS)?\/([\d.]+)/i.exec(ua);
  const chrome = /Chrome\/([\d.]+)/i.exec(ua);
  const firefox = /Firefox\/([\d.]+)/i.exec(ua);
  const safari = /Version\/([\d.]+).*Safari/i.exec(ua);

  if (edge) return { browserName: 'Edge', browserVersion: edge[2] };
  if (chrome && !/Edge/i.test(ua))
    return { browserName: 'Chrome', browserVersion: chrome[1] };
  if (firefox) return { browserName: 'Firefox', browserVersion: firefox[1] };
  if (safari && !/Chrom/i.test(ua))
    return { browserName: 'Safari', browserVersion: safari[1] };
  return { browserName: 'Unknown' };
};

const parseOS = (ua: string): { osName: string; osVersion?: string } => {
  const android = /Android\s([\d.]+)/i.exec(ua);
  const ios = /(?:CPU )?iPhone OS\s([\d_]+)/i.exec(ua);
  const ipad = /iPad; CPU OS\s([\d_]+)/i.exec(ua);
  const windows = /Windows NT\s([\d.]+)/i.exec(ua);
  const mac = /Mac OS X\s([\d_]+)/i.exec(ua);

  if (android) return { osName: 'Android', osVersion: android[1] };
  if (ios) return { osName: 'iOS', osVersion: ios[1].replace(/_/g, '.') };
  if (ipad) return { osName: 'iOS', osVersion: ipad[1].replace(/_/g, '.') };
  if (windows) return { osName: 'Windows', osVersion: windows[1] };
  if (mac) return { osName: 'macOS', osVersion: mac[1].replace(/_/g, '.') };
  return { osName: 'Unknown' };
};

/* -----------------------------------------------------------
 *  Detección WebGL (MUY IMPORTANTE)
 * --------------------------------------------------------- */

const getWebGLInfo = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      (canvas.getContext('webgl') as
        | WebGLRenderingContext
        | WebGL2RenderingContext
        | null) ??
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
    if (!gl) return {};

    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      vendor: ext
        ? (gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) as string)
        : null,
      renderer: ext
        ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string)
        : null,
    };
  } catch {
    return {};
  }
};

/* -----------------------------------------------------------
 *  FLAGS DE ATAQUES Y AUTOMATIZACIÓN
 * --------------------------------------------------------- */

const detectKeywords = (ua: string) => {
  const list = [
    'HeadlessChrome',
    'Puppeteer',
    'Playwright',
    'PhantomJS',
    'Selenium',
    'Scrapy',
    'curl/',
    'Wget',
    'python-requests',
    'Go-http-client',
    'Java/',
    'node-fetch',
  ];

  const found = list.filter((x) => new RegExp(x, 'i').test(ua));

  return {
    keywords: found,
    headless: found.some((x) =>
      /HeadlessChrome|Puppeteer|Playwright|PhantomJS/i.test(x),
    ),
    automatedClient: found.some((x) =>
      /(curl\/|Wget|python-requests|Scrapy|Go-http-client|Java\/|node-fetch)/i.test(
        x,
      ),
    ),
  };
};

const isInconsistentMobile = (uaInfo: UAInfo, screen: ScreenInfo) => {
  if (!screen.innerWidth || !screen.innerHeight) return false;

  const claimsMobile =
    /Mobile|Android|iPhone|iPad|iPod/.test(uaInfo.ua) ||
    uaInfo.deviceType !== 'desktop';

  return claimsMobile && Number(screen.innerWidth) >= 768;
};

const isImprobableResolution = (screen: ScreenInfo, deviceType: DeviceType) => {
  if (!screen.innerWidth || !screen.innerHeight) return false;

  const res = `${screen.innerWidth}x${screen.innerHeight}`;

  const banned = ['1024x768', '800x600', '1280x720'];

  if (banned.includes(res)) return true;

  // Si no es desktop, resoluciones grandes son sospechosas
  if (deviceType !== 'desktop' && Number(screen.innerWidth) >= 768) return true;

  return false;
};

const isSuspiciousRenderer = (renderer?: string) => {
  if (!renderer) return false;
  return (
    renderer.includes('SwiftShader') ||
    renderer.includes('Software') ||
    (renderer.includes('Google') && renderer.includes('ANGLE'))
  );
};

/* -----------------------------------------------------------
 *  BUILD META
 * --------------------------------------------------------- */

export const buildAntiBotMeta = (): AntiBotMeta => {
  const nav = navigator as ExtendedNavigator;
  const uaInfo = {
    ...parseBrowser(nav.userAgent || ''),
    ...parseOS(nav.userAgent || ''),
    ua: nav.userAgent || '',
    deviceType: detectDeviceType(),
    deviceBrand: undefined,
    deviceModel: undefined,
  };

  const screen: ScreenInfo = {
    innerWidth: window.innerWidth.toString(),
    innerHeight: window.innerHeight.toString(),
    screenWidth: window.screen.width.toString(),
    screenHeight: window.screen.height.toString(),
    devicePixelRatio: (window.devicePixelRatio || 1).toString(),
  };

  const system: SystemInfo = {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hardwareConcurrency:
      typeof nav.hardwareConcurrency === 'number'
        ? nav.hardwareConcurrency
        : null,
    deviceMemory:
      typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    touchPoints:
      typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : null,
  };

  const network: NetworkInfo = {
    effectiveType: nav.connection?.effectiveType ?? null,
    downlink: nav.connection?.downlink ?? null,
    rtt: nav.connection?.rtt ?? null,
  };

  const webgl = getWebGLInfo();

  const signals: AutomationSignals = {
    webdriver: nav.webdriver,
    pluginCount: nav.plugins.length || 0,
    languagesCount: nav.languages.length || 0,
    platform: nav.userAgentData?.platform,
    brands: nav.userAgentData?.brands?.map((b) => b.brand),
    webglVendor: webgl.vendor ?? undefined,
    webglRenderer: webgl.renderer ?? undefined,
  };

  const kw = detectKeywords(nav.userAgent || '');

  const flags = {
    headless: kw.headless,
    automatedClient: kw.automatedClient,
    inconsistentMobile: isInconsistentMobile(uaInfo, screen),
    improbableResolution: isImprobableResolution(screen, uaInfo.deviceType),
    suspiciousRenderer: isSuspiciousRenderer(signals.webglRenderer),
  };

  /* -------------------------------------
   * SCORE (mejorado)
   * ----------------------------------- */

  let score = 0;
  const reasons: string[] = [];

  if (flags.headless) ((score += 50), reasons.push('headless'));
  if (flags.automatedClient) ((score += 40), reasons.push('scraper-client'));
  if (signals.webdriver) ((score += 30), reasons.push('webdriver'));
  if (signals.pluginCount === 0) ((score += 10), reasons.push('zero-plugins'));
  if (flags.inconsistentMobile)
    ((score += 20), reasons.push('fake-mobile-resolution'));
  if (flags.improbableResolution)
    ((score += 15), reasons.push('improbable-resolution'));
  if (flags.suspiciousRenderer)
    ((score += 25), reasons.push('software-renderer'));

  if (score > 100) score = 100;

  const riskLevel = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';

  return {
    uaInfo,
    screen,
    system,
    network,
    signals,
    keywords: kw.keywords,
    flags,
    riskScore: score,
    riskLevel,
    reasons,
  };
};

/* -----------------------------------------------------------
 *  INTERCEPTOR AXIOS
 * --------------------------------------------------------- */

// export const setupAntiBotMetaInterceptor = (api?: AxiosInstance) => {
//   if (!api) return;

//   api.interceptors.request.use((config) => {
//     const meta = buildAntiBotMeta();

//     const compressed = btoa(JSON.stringify(meta));

//     config.headers = config.headers || {};
//     const headers = config.headers as any;

//     headers['X-AntiBot'] = compressed;
//     headers['X-AntiBot-Score'] = String(meta.riskScore);

//     return config;
//   });
// };
